/**
 * SceneTreeV2 — fully virtualised Scene Tree panel.
 *
 * Renders a `SceneHierarchyManifest` (or null) as an ARIA tree widget with
 * ≤ 500 DOM-mounted rows at any time, using `@tanstack/react-virtual`.
 *
 * This component is gated behind the `featureFlagEnabled` field in
 * `useSceneHierarchyStore` per DEC-91-02. The existing `SceneTreePanel.tsx`
 * is untouched.
 *
 * Feature-flag usage:
 * ```tsx
 * const enabled = useSceneHierarchyStore((s) => s.featureFlagEnabled);
 * if (!enabled) return <SceneTreePanel ... />;      // existing panel
 * return <SceneTreeV2 manifest={manifest} ... />;   // new panel
 * ```
 *
 * Props:
 *   - `manifest`        — the loaded `SceneHierarchyManifest`, or null.
 *   - `selectedNodeId`  — currently selected node id (controlled).
 *   - `onSelect`        — called when the user selects a node.
 *   - `isLoading`       — shows a loading skeleton when true.
 *
 * DOM virtualisation: `useVirtualizer` from `@tanstack/react-virtual` v3.
 * Row estimateSize: 40px (tokens.density.touchMin). Dynamic heights supported.
 *
 * Sources: 50-ui-design-brief.md §3; 20-two-tier-architecture.md §8.3; DEC-50-01.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { tokens } from "@/design";
import type {
  ControlOverlayEntry,
  SceneHierarchyManifest,
  TreeNode,
  UniversalSceneNode,
} from "@/sdk-stub/gtwin-sdk/scene-hierarchy-types";
import { projectTreeToFlatList, type FlatRow, type TreeAspect, type ShowMoreOffsets } from "./treeProjection";
import { SceneTreeNode } from "./SceneTreeNode";
import { isCatalogLayerVisibilityNode } from "./layerVisibilityNode";
import { SCENE_TREE_BUDGETS } from "./budgets";
import { collectAncestors, indexNodesById } from "./overlayGraph";
import { SceneTreeSearchBar } from "./SceneTreeSearchBar";
import { FilterChips, type ActiveFilters } from "./FilterChips";

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

export interface SceneTreeV2Props {
  readonly manifest: SceneHierarchyManifest | null;
  readonly selectedNodeId?: string | null;
  readonly onSelect: (nodeId: string | null) => void;
  readonly isLoading?: boolean;
  /**
   * Called when an `asset` node (or one with `metadata.hierarchyDomain` /
   * `metadata.hierarchyUrl`) is expanded for the first time and its children
   * are not yet present in the manifest.
   *
   * Resolves to `"not-implemented"` when no adapter handles this node type —
   * shows "No further detail available" (NOT the error row).
   * Rejects on genuine load failures — shows error row + Retry button.
   */
  readonly onLazyLoad?: (node: UniversalSceneNode) => Promise<"not-implemented" | void>;
  /**
   * Block 2 — per-object control-overlay entries keyed by `objectId`.
   * Each rendered row receives `overlayByObject[node.objectId]` so it can
   * reflect visibility / isolation / highlight / colour-override state.
   * Absent / undefined means "no overlay applied".
   */
  readonly overlayByObject?: Record<string, ControlOverlayEntry>;
  /**
   * Catalog mount-gate visibility (`useViewerStore.visibleLayerIds`). Layer
   * root rows use this for the eye icon instead of overlay defaults.
   */
  readonly visibleLayerIds?: ReadonlySet<string>;
  /**
   * Block 2 — called when a row's visibility toggle is activated. When
   * provided, each `SceneTreeNode` renders its visibility button. Omit to
   * hide the per-row visibility control entirely.
   */
  readonly onToggleVisibility?: (nodeId: string) => void;
  /**
   * Block 2, Task C — "Isolate system". Called with the selected node id;
   * the handler isolates that node's functional-aspect subtree. When omitted
   * (or no node selected) the button is not actionable.
   */
  readonly onIsolateSystem?: (selectedNodeId: string) => void;
  /**
   * Block 2, Task C — "Color by aspect". Called with the CURRENTLY ACTIVE
   * aspect so the handler can colour nodes by their top-level group in that
   * aspect.
   */
  readonly onColorByAspect?: (aspect: TreeAspect) => void;
  /** Block 2, Task C — clears any active isolation / colour overlay. */
  readonly onClearOverlay?: () => void;
  /** P3 — opacity change handler. Called with (objectId, value 0–1). */
  readonly onOpacityChange?: (objectId: string, value: number) => void;
  /** P3 — zoom-to handler (emits engine navigation event). */
  readonly onZoomTo?: (nodeId: string) => void;
  /** Opens right-rail layer properties/settings for the row. */
  readonly onOpenSettings?: (nodeId: string) => void;
  /** P3 — isolate handler (for context menu). */
  readonly onIsolate?: (nodeId: string) => void;
  /** P3 — show-all handler (clears all hides). */
  readonly onShowAll?: () => void;
  /** Task 2 — called when a node is collapsed so the container can evict Tier-2 data. */
  readonly onCollapse?: (nodeId: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Lazy-load types
// ─────────────────────────────────────────────────────────────────────────────

/** Per-node load lifecycle for lazy Tier-2 expansion. */
type LoadState = "idle" | "loading" | "loaded" | "error" | "not-implemented";

/** Synthetic row shown while a Tier-2 sub-tree is being fetched. */
interface LazyLoadingFlatRow {
  readonly kind: "lazy-loading";
  readonly nodeId: string;
  readonly depth: number;
}

/** Synthetic row shown when a Tier-2 fetch fails. */
interface LazyErrorFlatRow {
  readonly kind: "lazy-error";
  readonly nodeId: string;
  readonly depth: number;
}

/** Synthetic row shown when no adapter supports a node's domain. */
interface LazyNotImplementedFlatRow {
  readonly kind: "lazy-not-implemented";
  readonly nodeId: string;
  readonly depth: number;
}

type CombinedRow =
  | FlatRow
  | LazyLoadingFlatRow
  | LazyErrorFlatRow
  | LazyNotImplementedFlatRow;

// ─────────────────────────────────────────────────────────────────────────────
// Empty / Loading states
// ─────────────────────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      role="status"
      aria-label="No scene items"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        gap: tokens.space[3],
        padding: tokens.space[5],
        color: tokens.color.ink.muted,
        textAlign: "center",
      }}
    >
      <span
        aria-hidden
        style={{
          fontFamily: "Material Symbols Rounded",
          fontSize: 32,
          lineHeight: 1,
        }}
      >
        account_tree
      </span>
      <span
        style={{
          fontSize: tokens.font.size.sm,
          fontWeight: tokens.font.weight.medium,
        }}
      >
        No scene items yet.
      </span>
      <span
        style={{
          fontSize: tokens.font.size.xs,
          color: tokens.color.ink.muted,
        }}
      >
        Load a manifest to see the hierarchy.
      </span>
    </div>
  );
}

function LoadingState() {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Loading scene tree"
      style={{ padding: tokens.space[3], display: "flex", flexDirection: "column", gap: tokens.space[2] }}
    >
      {[80, 60, 70, 50, 65].map((width, i) => (
        <div
          key={i}
          style={{
            height: tokens.density.touchMin,
            width: `${width}%`,
            borderRadius: tokens.radius.sm,
            background: tokens.color.surface[2],
            animation: "scene-tree-shimmer 1.4s linear infinite",
          }}
        />
      ))}
    </div>
  );
}

function ShowMoreRow({
  remaining,
  depth,
  onShowMore,
  parentId,
}: {
  remaining: number;
  depth: number;
  parentId: string | null;
  onShowMore: (parentId: string | null) => void;
}) {
  return (
    <button
      aria-label={`Show ${SCENE_TREE_BUDGETS.MAX_PER_LEVEL} more children, ${remaining} remaining`}
      onClick={() => onShowMore(parentId)}
      style={{
        display: "flex",
        alignItems: "center",
        height: tokens.density.touchMin,
        paddingLeft: depth * 16 + tokens.density.touchMin,
        paddingRight: tokens.space[2],
        gap: tokens.space[2],
        background: "none",
        border: "none",
        cursor: "pointer",
        color: tokens.color.accent.primary,
        fontSize: tokens.font.size.sm,
        fontWeight: tokens.font.weight.medium,
        fontFamily: tokens.font.family.ui,
        width: "100%",
        textAlign: "left",
      }}
    >
      <span
        aria-hidden
        style={{ fontFamily: "Material Symbols Rounded", fontSize: tokens.font.size.md, lineHeight: 1 }}
      >
        expand_more
      </span>
      {`Show ${SCENE_TREE_BUDGETS.MAX_PER_LEVEL} more (${remaining} remaining)`}
    </button>
  );
}

function LazyLoadingRow({ depth }: { depth: number }) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Loading sub-tree"
      aria-live="polite"
      style={{
        display: "flex",
        alignItems: "center",
        height: tokens.density.touchMin,
        paddingLeft: depth * 16 + 8,
        gap: tokens.space[2],
      }}
    >
      <div
        style={{
          height: tokens.font.size.sm,
          width: "55%",
          borderRadius: tokens.radius.sm,
          background: tokens.color.surface[2],
          animation: "scene-tree-shimmer 1.4s linear infinite",
        }}
      />
    </div>
  );
}

function LazyErrorRow({
  depth,
  nodeId,
  onRetry,
}: {
  depth: number;
  nodeId: string;
  onRetry: (nodeId: string) => void;
}) {
  return (
    <div
      role="alert"
      style={{
        display: "flex",
        alignItems: "center",
        height: tokens.density.touchMin,
        paddingLeft: depth * 16 + 8,
        gap: tokens.space[2],
        color: tokens.color.semantic.danger,
        fontSize: tokens.font.size.sm,
        fontFamily: tokens.font.family.ui,
      }}
    >
      <span>Failed to load</span>
      <button
        onClick={() => onRetry(nodeId)}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          color: tokens.color.accent.primary,
          fontSize: tokens.font.size.sm,
          fontFamily: tokens.font.family.ui,
          fontWeight: tokens.font.weight.medium,
          padding: 0,
        }}
      >
        Retry
      </button>
    </div>
  );
}

function LazyNotImplementedRow({ depth }: { depth: number }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        height: tokens.density.touchMin,
        paddingLeft: depth * 16 + 8,
        color: tokens.color.ink.muted,
        fontSize: tokens.font.size.sm,
        fontFamily: tokens.font.family.ui,
        fontStyle: "italic",
      }}
    >
      No further detail available
    </div>
  );
}

/**
 * Returns true when a node should trigger a lazy Tier-2 fetch on first expand.
 * Conditions: `hasChildren === true`, kind is `"asset"` or the node carries
 * hierarchy-domain / hierarchy-URL metadata, AND no children already exist in
 * the manifest (avoids re-fetching already-populated nodes).
 */
function shouldLazyLoad(
  node: UniversalSceneNode,
  manifest: SceneHierarchyManifest | null,
): boolean {
  if (!node.hasChildren) return false;
  const alreadyLoaded =
    manifest?.nodes.some((n) => n.parentId === node.id) ?? false;
  if (alreadyLoaded) return false;
  return (
    node.kind === "asset" ||
    typeof node.metadata?.hierarchyDomain === "string" ||
    typeof node.metadata?.hierarchyUrl === "string"
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Aspect toggle — Spatial / Functional segmented control
// ─────────────────────────────────────────────────────────────────────────────

const ASPECT_OPTIONS: { value: TreeAspect; label: string }[] = [
  { value: "spatial", label: "Spatial" },
  { value: "functional", label: "Functional" },
];

function AspectToggle({
  activeAspect,
  onAspectChange,
}: {
  activeAspect: TreeAspect;
  onAspectChange: (aspect: TreeAspect) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Tree aspect"
      style={{
        display: "flex",
        flexShrink: 0,
        gap: tokens.space[1],
        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
        backgroundColor: tokens.color.surface[1],
        borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
      }}
    >
      {ASPECT_OPTIONS.map((opt) => {
        const isActive = opt.value === activeAspect;
        return (
          <button
            key={opt.value}
            role="radio"
            aria-checked={isActive}
            aria-label={`Show ${opt.label} hierarchy`}
            data-aspect={opt.value}
            onClick={() => onAspectChange(opt.value)}
            style={{
              flex: 1,
              height: tokens.density.rowDense,
              border: "none",
              borderRadius: tokens.radius.pill,
              cursor: "pointer",
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.size.xs,
              fontWeight: isActive
                ? tokens.font.weight.semibold
                : tokens.font.weight.regular,
              color: isActive
                ? tokens.color.accent.primary
                : tokens.color.ink.muted,
              backgroundColor: isActive
                ? tokens.color.accent.subtle
                : "transparent",
              boxShadow: isActive
                ? tokens.elevation[1]
                : "none",
              transition: `background-color ${tokens.motion.duration.fast}ms ${tokens.motion.easing.standard}, color ${tokens.motion.duration.fast}ms ${tokens.motion.easing.standard}`,
              padding: `0 ${tokens.space[2]}px`,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Overlay action bar — Block 2, Task C ("Isolate system" / "Color by aspect")
// ─────────────────────────────────────────────────────────────────────────────

function OverlayActionBar({
  activeAspect,
  selectedNodeId,
  onIsolateSystem,
  onColorByAspect,
  onClearOverlay,
}: {
  activeAspect: TreeAspect;
  selectedNodeId: string | null | undefined;
  onIsolateSystem?: (selectedNodeId: string) => void;
  onColorByAspect?: (aspect: TreeAspect) => void;
  onClearOverlay?: () => void;
}) {
  // Render nothing when no Block 2 action handlers are wired.
  if (!onIsolateSystem && !onColorByAspect && !onClearOverlay) return null;

  const btnStyle: CSSProperties = {
    height: tokens.density.rowDense - tokens.space[1],
    padding: `0 ${tokens.space[2]}px`,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.pill,
    background: tokens.color.surface[1],
    color: tokens.color.ink.secondary,
    cursor: "pointer",
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.xs,
    fontWeight: tokens.font.weight.medium,
    whiteSpace: "nowrap",
  };

  return (
    <div
      role="group"
      aria-label="Overlay actions"
      style={{
        display: "flex",
        flexShrink: 0,
        gap: tokens.space[1],
        padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
        backgroundColor: tokens.color.surface[1],
        borderBottom: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        overflowX: "auto",
      }}
    >
      {onIsolateSystem && (
        <button
          type="button"
          data-testid="isolate-system-button"
          aria-label="Isolate system of selected node"
          disabled={!selectedNodeId}
          onClick={() => selectedNodeId && onIsolateSystem(selectedNodeId)}
          style={{
            ...btnStyle,
            cursor: selectedNodeId ? "pointer" : "not-allowed",
            opacity: selectedNodeId ? 1 : 0.5,
          }}
        >
          Isolate system
        </button>
      )}
      {onColorByAspect && (
        <button
          type="button"
          data-testid="color-by-aspect-button"
          aria-label={`Color by ${activeAspect} aspect`}
          onClick={() => onColorByAspect(activeAspect)}
          style={btnStyle}
        >
          Color by aspect
        </button>
      )}
      {onClearOverlay && (
        <button
          type="button"
          data-testid="clear-overlay-button"
          aria-label="Clear overlay"
          onClick={() => onClearOverlay()}
          style={btnStyle}
        >
          Clear
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export function SceneTreeV2({
  manifest,
  selectedNodeId,
  onSelect,
  isLoading = false,
  onLazyLoad,
  overlayByObject,
  visibleLayerIds,
  onToggleVisibility,
  onIsolateSystem,
  onColorByAspect,
  onClearOverlay,
  onOpacityChange,
  onZoomTo,
  onOpenSettings,
  onIsolate,
  onShowAll,
  onCollapse,
}: SceneTreeV2Props) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Stable ref to the latest manifest — lets load callbacks access it
  // without adding manifest to their dependency arrays.
  const manifestRef = useRef(manifest);
  manifestRef.current = manifest;

  // Expansion state — Set of expanded node ids.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Active aspect for the tree projection (Block 1).
  const [activeAspect, setActiveAspect] = useState<TreeAspect>("spatial");

  // Per-node Tier-2 load state (only populated when onLazyLoad is provided).
  const [nodeLoadStates, setNodeLoadStates] = useState<
    Map<string, LoadState>
  >(new Map());

  // Per-parent "show more" offset state (Task 1).
  const [showMoreOffsets, setShowMoreOffsets] = useState<Map<string | null, number>>(new Map());

  // Search query state (Task 3).
  const [searchQuery, setSearchQuery] = useState("");

  // Filter chips state (Task 3).
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({
    domains: new Set(),
    lorLevels: new Set(),
  });

  // Context menu state (Task 4).
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    nodeId: string;
  } | null>(null);

  // Build a nodeById index for search ancestor inclusion.
  const nodeByIdMap = useMemo(
    () => (manifest ? indexNodesById(manifest.nodes) : new Map()),
    [manifest],
  );

  // Filtered nodes based on search + filter chips.
  const filteredNodes = useMemo(() => {
    if (!manifest) return [];
    let nodes = manifest.nodes;

    const hasActiveFilters =
      activeFilters.domains.size > 0 || activeFilters.lorLevels.size > 0;

    if (hasActiveFilters) {
      nodes = nodes.filter((n) => {
        if (activeFilters.domains.size > 0 && !activeFilters.domains.has(n.domain)) return false;
        if (activeFilters.lorLevels.size > 0 && !activeFilters.lorLevels.has(n.lor)) return false;
        return true;
      });
    }

    if (!searchQuery.trim()) return nodes;

    const q = searchQuery.trim().toLowerCase();
    const matchIds = new Set<string>();

    for (const node of nodes) {
      const haystack = [node.label];
      if (node.standardRef?.nativeId) haystack.push(node.standardRef.nativeId);
      if (node.designation?.functional) haystack.push(node.designation.functional);
      if (node.designation?.product) haystack.push(node.designation.product);
      if (node.designation?.location) haystack.push(node.designation.location);
      if (haystack.some((s) => s.toLowerCase().includes(q))) {
        matchIds.add(node.id);
        const ancestors = collectAncestors(node.id, nodeByIdMap, activeAspect);
        for (const aid of ancestors) matchIds.add(aid);
      }
    }

    return manifest.nodes.filter((n) => matchIds.has(n.id));
  }, [manifest, searchQuery, activeFilters, nodeByIdMap, activeAspect]);

  // Flat projected list of visible rows, post-processed with lazy-load sentinels.
  const rows: CombinedRow[] = useMemo(() => {
    const base: CombinedRow[] = filteredNodes.length > 0 || (!searchQuery.trim() && manifest)
      ? projectTreeToFlatList(
          searchQuery.trim() || activeFilters.domains.size > 0 || activeFilters.lorLevels.size > 0
            ? filteredNodes
            : manifest?.nodes ?? [],
          expandedIds,
          activeAspect,
          showMoreOffsets as ShowMoreOffsets,
        )
      : [];

    if (nodeLoadStates.size === 0) return base;

    const result: CombinedRow[] = [];
    for (const row of base) {
      result.push(row);
      if (row.kind === "node" && row.isExpanded) {
        const ls = nodeLoadStates.get(row.node.id);
        if (ls === "loading") {
          result.push({
            kind: "lazy-loading",
            nodeId: row.node.id,
            depth: row.depth + 1,
          });
        } else if (ls === "error") {
          result.push({
            kind: "lazy-error",
            nodeId: row.node.id,
            depth: row.depth + 1,
          });
        } else if (ls === "not-implemented") {
          result.push({
            kind: "lazy-not-implemented",
            nodeId: row.node.id,
            depth: row.depth + 1,
          });
        }
      }
    }
    return result;
  }, [manifest, expandedIds, nodeLoadStates, activeAspect]);

  const layerVisibilityByNodeId = useMemo(() => {
    const visibility = new Map<string, boolean>();
    if (!manifest || !visibleLayerIds) return visibility;

    const childrenByParent = new Map<string | null, TreeNode[]>();
    for (const node of manifest.nodes) {
      const siblings = childrenByParent.get(node.parentId) ?? [];
      siblings.push(node);
      childrenByParent.set(node.parentId, siblings);
    }

    const collectLayerIds = (node: TreeNode): string[] => {
      const ids: string[] = [];
      if (node.layerId && isCatalogLayerVisibilityNode(node)) ids.push(node.layerId);
      for (const child of childrenByParent.get(node.id) ?? []) {
        ids.push(...collectLayerIds(child));
      }
      return ids;
    };

    for (const node of manifest.nodes) {
      const layerIds = [...new Set(collectLayerIds(node))];
      if (layerIds.length > 0) {
        visibility.set(node.id, layerIds.some((layerId) => visibleLayerIds.has(layerId)));
      }
    }

    return visibility;
  }, [manifest, visibleLayerIds]);

  // TanStack Virtual virtualiser.
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => tokens.density.rowDense + tokens.border.width.thin * 2,
    overscan: 5,
  });

  // ── Reveal selected node (Block 2, Task E — bidirectional 3D ⇄ tree) ───────
  // When the selection changes (e.g. from a 3D dblclick pick), expand the
  // selected node's ancestor path in the active aspect so it becomes visible
  // in the projected list. Pure-logic core: ancestor collection. The scroll is
  // handled in a follow-up effect once the row exists.
  useEffect(() => {
    if (!selectedNodeId || !manifest) return;
    const nodeById = indexNodesById(manifest.nodes);
    if (!nodeById.has(selectedNodeId)) return;
    const ancestors = collectAncestors(selectedNodeId, nodeById, activeAspect);
    if (ancestors.length === 0) return;
    setExpandedIds((prev) => {
      // Only update state when at least one ancestor is newly expanded —
      // avoids an unnecessary re-render loop.
      let changed = false;
      const next = new Set(prev);
      for (const id of ancestors) {
        if (!next.has(id)) {
          next.add(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [selectedNodeId, manifest, activeAspect]);

  // Scroll the selected node into view once its row is present in the list.
  useEffect(() => {
    if (!selectedNodeId) return;
    const index = rows.findIndex(
      (r) => r.kind === "node" && r.node.id === selectedNodeId,
    );
    if (index >= 0) virtualizer.scrollToIndex?.(index);
  }, [selectedNodeId, rows, virtualizer]);

  // ── Lazy-load helpers ─────────────────────────────────────────────────────

  /** Initiates a lazy Tier-2 load for a node — sets loading state and fires the prop. */
  const triggerLazyLoad = useCallback(
    (nodeId: string, node: TreeNode) => {
      setNodeLoadStates((prev) => new Map(prev).set(nodeId, "loading"));
      onLazyLoad!(node)
        .then((result) => {
          setNodeLoadStates((prev) =>
            new Map(prev).set(
              nodeId,
              result === "not-implemented" ? "not-implemented" : "loaded",
            ),
          );
        })
        .catch(() => {
          setNodeLoadStates((prev) => new Map(prev).set(nodeId, "error"));
        });
    },
    [onLazyLoad],
  );

  /** Called by the LazyErrorRow Retry button — resets to idle and re-triggers. */
  const handleRetry = useCallback(
    (nodeId: string) => {
      const node = manifestRef.current?.nodes.find((n) => n.id === nodeId);
      if (!node || !onLazyLoad) return;
      triggerLazyLoad(nodeId, node);
    },
    [onLazyLoad, triggerLazyLoad],
  );

  // ── Expand / collapse ──────────────────────────────────────────────────────

  const handleToggleExpand = useCallback(
    (nodeId: string) => {
      const willExpand = !expandedIds.has(nodeId);

      setExpandedIds((prev) => {
        const next = new Set(prev);
        if (willExpand) next.add(nodeId);
        else next.delete(nodeId);
        return next;
      });

      if (!willExpand) {
        onCollapse?.(nodeId);
        return;
      }

      if (!onLazyLoad) return;

      const node = manifestRef.current?.nodes.find((n) => n.id === nodeId);
      if (!node || !shouldLazyLoad(node, manifestRef.current)) return;

      const currentLoadState =
        nodeLoadStates.get(nodeId) ?? "idle";
      if (currentLoadState !== "idle") return;

      triggerLazyLoad(nodeId, node);
    },
    [expandedIds, nodeLoadStates, onLazyLoad, onCollapse, triggerLazyLoad],
  );

  // "Show more" advances the offset for the given parent so the next
  // MAX_PER_LEVEL children become visible.
  const handleShowMore = useCallback((parentId: string | null) => {
    setShowMoreOffsets((prev) => {
      const next = new Map(prev);
      const current = next.get(parentId) ?? 0;
      next.set(parentId, current + SCENE_TREE_BUDGETS.MAX_PER_LEVEL);
      return next;
    });
  }, []);

  // ── Context menu (Task 4) ───────────────────────────────────────────────────

  const handleContextMenu = useCallback(
    (e: React.MouseEvent, nodeId: string) => {
      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY, nodeId });
    },
    [],
  );

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, [contextMenu]);

  // ── Keyboard navigation (roving tabindex) ─────────────────────────────────

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const nodeRows = rows.filter((r) => r.kind === "node");
      const currentIndex = nodeRows.findIndex(
        (r) => r.kind === "node" && r.node.id === selectedNodeId,
      );

      if (e.key === "ArrowDown") {
        e.preventDefault();
        const next = nodeRows[currentIndex + 1];
        if (next && next.kind === "node") onSelect(next.node.id);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prev = nodeRows[currentIndex - 1];
        if (prev && prev.kind === "node") onSelect(prev.node.id);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        const current = nodeRows[currentIndex];
        if (current?.kind === "node" && current.hasChildren && !current.isExpanded) {
          handleToggleExpand(current.node.id);
        } else if (current?.kind === "node" && current.isExpanded) {
          const firstChild = nodeRows[currentIndex + 1];
          if (firstChild?.kind === "node") onSelect(firstChild.node.id);
        }
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const current = nodeRows[currentIndex];
        if (current?.kind === "node" && current.isExpanded) {
          handleToggleExpand(current.node.id);
        } else if (current?.kind === "node") {
          // Move to parent
          const parent = nodeRows
            .slice(0, currentIndex)
            .reverse()
            .find((r) => r.kind === "node" && r.depth < current.depth);
          if (parent?.kind === "node") onSelect(parent.node.id);
        }
      } else if (e.key === "Escape") {
        onSelect(null);
      } else if (e.key === "Home") {
        e.preventDefault();
        const first = nodeRows[0];
        if (first?.kind === "node") onSelect(first.node.id);
      } else if (e.key === "End") {
        e.preventDefault();
        const last = nodeRows[nodeRows.length - 1];
        if (last?.kind === "node") onSelect(last.node.id);
      }
    },
    [rows, selectedNodeId, onSelect, handleToggleExpand],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  const containerStyle: CSSProperties = {
    height: "100%",
    overflow: "hidden",
    backgroundColor: tokens.color.surface[0],
    position: "relative",
    display: "flex",
    flexDirection: "column",
  };

  const scrollStyle: CSSProperties = {
    flex: 1,
    overflow: "auto",
    position: "relative",
    padding: `${tokens.space[1]}px 0 ${tokens.space[2]}px`,
    backgroundColor: tokens.color.surface[0],
  };

  if (isLoading) {
    return (
      <div style={containerStyle} data-testid="scene-tree-v2">
        <LoadingState />
      </div>
    );
  }

  if (!manifest) {
    return (
      <div style={containerStyle} data-testid="scene-tree-v2">
        <EmptyState />
      </div>
    );
  }

  const totalSize = virtualizer.getTotalSize();
  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div
      style={containerStyle}
      data-testid="scene-tree-v2"
    >
      {/* Aspect toggle — Spatial / Functional segmented control */}
      <AspectToggle activeAspect={activeAspect} onAspectChange={setActiveAspect} />

      {/* Block 2 overlay actions — Isolate system / Color by aspect / Clear */}
      <OverlayActionBar
        activeAspect={activeAspect}
        selectedNodeId={selectedNodeId}
        onIsolateSystem={onIsolateSystem}
        onColorByAspect={onColorByAspect}
        onClearOverlay={onClearOverlay}
      />

      {/* P3 — Search bar + filter chips */}
      <SceneTreeSearchBar query={searchQuery} onQueryChange={setSearchQuery} />
      <FilterChips
        manifest={manifest}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
      />

      {/* Scrollable virtualised tree */}
      <div
        ref={containerRef}
        style={scrollStyle}
        onKeyDown={handleKeyDown}
      >
      {/* ARIA tree wrapper — not the scroll container */}
      <div
        role="tree"
        aria-label="Scene Tree"
        aria-multiselectable="false"
        style={{
          height: totalSize,
          width: "100%",
          position: "relative",
        }}
      >
        {virtualItems.map((virtualItem) => {
          const row = rows[virtualItem.index];
          if (!row) return null;

          const itemStyle: CSSProperties = {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            transform: `translateY(${virtualItem.start}px)`,
          };

          if (row.kind === "show-more") {
            return (
              <div key={virtualItem.key} style={itemStyle}>
                <ShowMoreRow
                  remaining={row.remaining}
                  depth={row.depth}
                  parentId={row.parentId}
                  onShowMore={handleShowMore}
                />
              </div>
            );
          }

          if (row.kind === "lazy-loading") {
            return (
              <div key={virtualItem.key} style={itemStyle} aria-live="polite">
                <LazyLoadingRow depth={row.depth} />
              </div>
            );
          }

          if (row.kind === "lazy-error") {
            return (
              <div key={virtualItem.key} style={itemStyle}>
                <LazyErrorRow
                  depth={row.depth}
                  nodeId={row.nodeId}
                  onRetry={handleRetry}
                />
              </div>
            );
          }

          if (row.kind === "lazy-not-implemented") {
            return (
              <div key={virtualItem.key} style={itemStyle}>
                <LazyNotImplementedRow depth={row.depth} />
              </div>
            );
          }

          // row.kind === "node"
          return (
            <div key={virtualItem.key} style={itemStyle}>
              <SceneTreeNode
                row={row}
                selected={row.node.id === selectedNodeId}
                onToggleExpand={handleToggleExpand}
                onSelect={onSelect}
                overlay={overlayByObject?.[row.node.objectId]}
                catalogLayerVisible={layerVisibilityByNodeId.get(row.node.id)}
                onToggleVisibility={onToggleVisibility}
                onOpacityChange={onOpacityChange}
                onContextMenu={handleContextMenu}
                onZoomTo={onZoomTo}
                onOpenSettings={onOpenSettings}
                searchQuery={searchQuery}
              />
            </div>
          );
        })}
      </div>
      </div>

      {/* Context menu (Task 4) */}
      {contextMenu && (
        <div
          role="menu"
          aria-label="Node context menu"
          data-testid="scene-tree-context-menu"
          onClick={closeContextMenu}
          style={{
            position: "fixed",
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 9999,
            background: tokens.color.surface[1],
            border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
            borderRadius: tokens.radius.md,
            boxShadow: tokens.elevation[2],
            padding: `${tokens.space[1]}px 0`,
            minWidth: 160,
          }}
        >
          {onZoomTo && (
            <button
              role="menuitem"
              data-testid="ctx-zoom-to"
              onClick={() => { onZoomTo(contextMenu.nodeId); closeContextMenu(); }}
              style={contextMenuItemStyle}
            >
              <MaterialIconInline name="center_focus_strong" /> Zoom to
            </button>
          )}
          {onIsolate && (
            <button
              role="menuitem"
              data-testid="ctx-isolate"
              onClick={() => { onIsolate(contextMenu.nodeId); closeContextMenu(); }}
              style={contextMenuItemStyle}
            >
              <MaterialIconInline name="filter_center_focus" /> Isolate
            </button>
          )}
          {onToggleVisibility && (
            <button
              role="menuitem"
              data-testid="ctx-hide"
              onClick={() => { onToggleVisibility(contextMenu.nodeId); closeContextMenu(); }}
              style={contextMenuItemStyle}
            >
              <MaterialIconInline name="visibility_off" /> Hide
            </button>
          )}
          {onShowAll && (
            <button
              role="menuitem"
              data-testid="ctx-show-all"
              onClick={() => { onShowAll(); closeContextMenu(); }}
              style={contextMenuItemStyle}
            >
              <MaterialIconInline name="visibility" /> Show all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Context menu helpers
// ─────────────────────────────────────────────────────────────────────────────

const contextMenuItemStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
  background: "none",
  border: "none",
  cursor: "pointer",
  fontSize: tokens.font.size.sm,
  fontFamily: tokens.font.family.ui,
  color: tokens.color.ink.primary,
  textAlign: "left",
};

function MaterialIconInline({ name }: { name: string }) {
  return (
    <span
      aria-hidden
      style={{
        fontFamily: "Material Symbols Rounded",
        fontSize: tokens.font.size.md,
        lineHeight: 1,
      }}
    >
      {name}
    </span>
  );
}
